// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { Breakpoint } from '@mui/material/styles';
import { formatTime, utils } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import type { RecurringOrdersHomeQuery } from '../../types/__generated__/graphql';
import { getID, getNumericID, getTimeRanges } from '../../utils';
import { getRecurringOrderStatus } from '../../utils/getRecurringOrderStatus';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import OrderIdLink from '../order/OrderIdLink';
import RecurringOrderIdLink from './RecurringOrderIdLink';
import RecurringOrderOptions from './RecurringOrderOptions';
import formatRecurringFreq from './formatRecurringFreq';

type RecurringOrderData = RecurringOrdersHomeQuery['recurring_orders']['edges'][number]['node'];
interface Props {
  readonly orders: RecurringOrderData[];
  readonly userCanEditRecurringOrders: boolean;
}

export default function RecurringOrderTable({ orders, userCanEditRecurringOrders }: Props) {
  const { timezone } = useUserContext();
  const { todayEnd } = getTimeRanges(timezone);
  const idValues: React.ReactElement[] = [];
  const nextOrders: (React.ReactElement | string)[] = [];
  const frequencyValues: string[] = [];
  const statusValues: string[] = [];
  for (const order of orders) {
    const gotId = getID(order.id);
    idValues.push(<RecurringOrderIdLink id={gotId} title={order.customer ?? 'Unknown'} />);
    const nextOrder = order.child_orders.edges[0]?.node ?? undefined;
    if (nextOrder) {
      nextOrders.push(
        <OrderIdLink id={getID(nextOrder.id)} title={formatTime(nextOrder.order_fulfillment_time, { timezone })} />,
      );
    } else {
      nextOrders.push('');
    }
    frequencyValues.push(formatRecurringFreq(order.recurring_options));
    statusValues.push(
      getRecurringOrderStatus({
        endTime: order.recurring_end_time,
        startTime: order.recurring_start_time,
        todayEnd: todayEnd,
        status: order.status,
      }),
    );
  }
  const columns: ColumnType[] = [
    {
      name: 'Customer',
      values: idValues,
    },
    {
      name: 'Frequency',
      values: frequencyValues,
    },
    {
      name: 'Next Order',
      values: nextOrders,
      visibleAt: 'sm' as Breakpoint,
    },
    {
      name: 'Status',
      values: statusValues,
    },
    userCanEditRecurringOrders
      ? {
          name: 'Actions',
          smallName: '',
          numeric: true,
          flex: '0 0 auto',
          values: orders.map((order) => (
            <RecurringOrderOptions
              key={order.id}
              onSuccess={() => {
                // do nothing
              }}
              useMoreIcon
              recurringOrderId={getNumericID(order.id)}
              status={order.status}
            />
          )),
        }
      : undefined,
  ].filter(utils.notNull);

  return <Table columns={columns} />;
}
